import React, { useState, useEffect } from 'react';
import { X, BarChart3, Hash, Type, Calendar, Loader2, TrendingUp, TrendingDown, Percent } from 'lucide-react';
import { getAllStatistics } from '../utils/api';
import { useT } from '../i18n';

const ColumnStatistics = ({ onClose }) => {
  const t = useT();
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const result = await getAllStatistics();
      setStatistics(result.statistics || []);
      if (result.statistics && result.statistics.length > 0) {
        setSelectedColumn(result.statistics[0]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('stats.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (dtype) => {
    if (dtype.includes('int') || dtype.includes('float')) return Hash;
    if (dtype.includes('datetime')) return Calendar;
    return Type;
  };

  const getTypeBadge = (dtype) => {
    if (dtype.includes('int') || dtype.includes('float')) return 'badge-primary';
    if (dtype.includes('datetime')) return 'badge-accent';
    return 'badge-success';
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      if (Number.isInteger(value)) return value.toLocaleString();
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return String(value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content max-w-4xl" 
        onClick={(e) => e.stopPropagation()}
        data-testid="statistics-modal"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{t('stats.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('stats.subtitle')}</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="btn-ghost h-8 w-8 p-0" 
              data-testid="close-stats-modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">{error}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[240px,1fr] gap-6">
              {/* Column list */}
              <div className="space-y-1 max-h-[500px] overflow-y-auto pr-2">
                {statistics.map((stat) => {
                  const Icon = getTypeIcon(stat.dtype);
                  const isSelected = selectedColumn?.column === stat.column;
                  
                  return (
                    <button
                      key={stat.column}
                      onClick={() => setSelectedColumn(stat)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        isSelected 
                          ? 'bg-primary/10 text-primary' 
                          : 'hover:bg-muted'
                      }`}
                      data-testid={`stat-column-${stat.column}`}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm truncate flex-1">{stat.column}</span>
                      {stat.null_percentage > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {t('stats.nullShare', { percent: stat.null_percentage })}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Selected column details */}
              {selectedColumn && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">{selectedColumn.column}</h3>
                    <span className={`badge ${getTypeBadge(selectedColumn.dtype)}`}>
                      {selectedColumn.dtype}
                    </span>
                  </div>

                  {/* Overview stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="card p-3 stat-card">
                      <div className="text-xs text-muted-foreground mb-1">{t('stats.totalCount')}</div>
                      <div className="text-lg font-semibold">{formatValue(selectedColumn.total_count)}</div>
                    </div>
                    <div className="card p-3 stat-card">
                      <div className="text-xs text-muted-foreground mb-1">{t('stats.uniqueValues')}</div>
                      <div className="text-lg font-semibold">{formatValue(selectedColumn.unique_count)}</div>
                    </div>
                    <div className="card p-3 stat-card">
                      <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        <Percent className="h-3 w-3" /> Unique
                      </div>
                      <div className="text-lg font-semibold">{formatValue(selectedColumn.unique_percentage)}%</div>
                    </div>
                    <div className="card p-3 stat-card">
                      <div className="text-xs text-muted-foreground mb-1">{t('stats.nullValues')}</div>
                      <div className="text-lg font-semibold flex items-center gap-1">
                        {formatValue(selectedColumn.null_count)}
                        <span className="text-xs text-muted-foreground">({formatValue(selectedColumn.null_percentage)}%)</span>
                      </div>
                    </div>
                  </div>

                  {/* Numeric stats */}
                  {selectedColumn.is_numeric && (
                    <div className="card p-4">
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Hash className="h-4 w-4" />
                        {t('stats.numericTitle')}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <TrendingDown className="h-3 w-3" /> {t('stats.minimum')}
                          </div>
                          <div className="font-medium">{formatValue(selectedColumn.min)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" /> {t('stats.maximum')}
                          </div>
                          <div className="font-medium">{formatValue(selectedColumn.max)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">{t('stats.mean')}</div>
                          <div className="font-medium">{formatValue(selectedColumn.mean)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">{t('stats.median')}</div>
                          <div className="font-medium">{formatValue(selectedColumn.median)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">{t('stats.stdDev')}</div>
                          <div className="font-medium">{formatValue(selectedColumn.std)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">{t('stats.sum')}</div>
                          <div className="font-medium">{formatValue(selectedColumn.sum)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Date range. Its own block rather than the numeric one:
                      a date column has a first and a last, but no mean. */}
                  {selectedColumn.is_datetime && (
                    <div className="card p-4" data-testid="stats-dates">
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {t('stats.datesTitle')}
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <TrendingDown className="h-3 w-3" /> {t('stats.earliest')}
                          </div>
                          <div className="font-medium">{formatValue(selectedColumn.min)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" /> {t('stats.latest')}
                          </div>
                          <div className="font-medium">{formatValue(selectedColumn.max)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Text stats */}
                  {!selectedColumn.is_numeric && selectedColumn.top_values && (
                    <div className="card p-4">
                      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Type className="h-4 w-4" />
                        {t('stats.topValues')}
                      </h4>
                      <div className="space-y-2">
                        {selectedColumn.top_values.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="h-6 rounded bg-primary/10 relative overflow-hidden">
                                <div 
                                  className="h-full bg-primary/30 rounded"
                                  style={{ 
                                    width: `${(item.count / selectedColumn.top_values[0].count) * 100}%` 
                                  }}
                                />
                                <span className="absolute inset-0 px-2 flex items-center text-xs truncate">
                                  {item.value}
                                </span>
                              </div>
                            </div>
                            <span className="text-sm font-medium text-muted-foreground w-16 text-right">
                              {item.count.toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {/* String length stats */}
                      {selectedColumn.min_length !== undefined && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <h5 className="text-xs font-medium text-muted-foreground mb-2">{t('stats.stringLength')}</h5>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">{t('stats.lengthMin')}</span>{' '}
                              <span className="font-medium">{selectedColumn.min_length}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">{t('stats.lengthMax')}</span>{' '}
                              <span className="font-medium">{selectedColumn.max_length}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">{t('stats.lengthAvg')}</span>{' '}
                              <span className="font-medium">{selectedColumn.avg_length}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColumnStatistics;
